import { Component, type ErrorInfo, type ReactNode } from 'react';

const CHUNK_RELOAD_KEY = 'neozoo:chunk-reload';
const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export function clearChunkReloadGuard() {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Storage can be unavailable in strict privacy modes.
  }
}

export function reloadForUpdatedApplication(): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - lastReload < 30_000) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // Reload anyway when storage cannot be used.
  }

  window.location.reload();
  return true;
}

export function recoverFromChunkError(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : String(reason ?? '');
  if (!CHUNK_ERROR_PATTERN.test(message)) return false;

  return reloadForUpdatedApplication();
}

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled application error:', error, info);

    recoverFromChunkError(error);
  }

  private retry = () => {
    clearChunkReloadGuard();
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <section className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
            <i className="ri-refresh-line text-xl" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900">La aplicacion necesita recargarse</h1>
          <p className="mt-2 text-sm text-slate-600">
            Se publico una version nueva o ocurrio un error inesperado. Tus datos guardados no se pierden.
          </p>
          <button
            type="button"
            onClick={this.retry}
            className="mt-5 rounded-lg bg-[#0F5132] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#0A3D26]"
          >
            Recargar aplicacion
          </button>
        </section>
      </main>
    );
  }
}
